import React, { useRef } from "react";
import { Button, Box, Typography } from "@mui/material";
import { toast } from "react-toastify";
import Image from "src/shared/Image";

interface ImageUploaderProps {
  value: File | null;
  previewImage: string | null;
  onChange: (file: File | null) => void;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({ value, previewImage, onChange }) => {
  const uploadRef = useRef<HTMLInputElement>(null);
  const MAX_FILE_SIZE_MB = 6;
  const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please upload a valid image file.", {
        position: "top-right",
        autoClose: 3000,
      });
      e.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`, {
        position: "top-right",
        autoClose: 3000,
      });
      e.target.value = "";
      return;
    }
    onChange(file);
  };

  const handleRemoveImage = () => {
    onChange(null);
    if (uploadRef.current) {
      uploadRef.current.value = "";
    }
  };

  const previewUrl = previewImage || (value ? URL.createObjectURL(value) : null);

  console.log(previewUrl?.split(":"), previewUrl?.split(":").includes("data"))

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <input
          type="file"
          accept="image/*"
          ref={uploadRef}
          onChange={handleFile}
          hidden
        />

        <Button
          variant="outlined"
          onClick={() => uploadRef.current?.click()}
        >
          Upload Image
        </Button>

        {previewUrl && (
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            {previewUrl.split(":").includes("data") ? <img
              src={previewUrl}
              alt="Preview"
              style={{
                width: 50,
                height: 50,
                objectFit: 'cover',
                borderRadius: 4,
                border: '1px solid #ddd'
              }}
            /> : <div className="w-20 h-20">
              <Image imagePath={previewUrl} />
            </div>
            }

            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={handleRemoveImage}
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                minWidth: 'auto',
                width: 20,
                height: 20,
                borderRadius: '50%',
                fontSize: '12px',
                p: 0
              }}
            >
              ×
            </Button>
          </Box>
        )}
      </Box>

      {previewUrl && value && (
        <Box sx={{ pl: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
            Image Preview:
          </Typography>
          <Typography variant="caption" color="text.secondary">
            File: {value.name} ({(value.size / (1024 * 1024)).toFixed(2)} MB)
          </Typography>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary">
        Maximum file size: {MAX_FILE_SIZE_MB}MB
      </Typography>
    </Box>
  );
};

export default ImageUploader;